import { Avatar, Box, IconButton, MenuItem, Typography } from '@mui/material'
import React, { memo, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { LocalizationProvider, TimeField } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Driver, TimeSheetItem } from '../../store/slices/timesheet/timesheetDetailSlice'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'

export interface TableTitleInterface {
	title1?: string
	title2?: string
	title3?: string
	filter?: boolean
	order?: 'TimeSheetDate' | 'DriverName' | 'DriverId' | 'InternalNumber'
	activeColumn?: 'TimeSheetDate' | 'DriverName' | 'DriverId' | 'InternalNumber'
}

export interface TableTimesInterface {
	times1?: string | number
	times2?: string | number
	times3?: string | number
}

export interface TableKmsInterface {
	kms1?: string | number
	kms2?: string | number
	kms3?: string | number
}

export interface TablePassengersInterface {
	pass1?: string | number
	pass2?: string | number
	pass3?: string | number
}

export interface TableMotoristaInterface {
	img?: string
	name: string
	status: string
}

export interface TableDataInterface {
	date: string
	services: number
	rowInfo?: TimeSheetItem
}

export interface TableTimeInterface {
	initialTime: string
	finalTime: string
	type?: string
	journey?: string
	initialFunction: (newValue: Dayjs | null) => void
}

export enum Submit {
	NotSubmitted = 'Não Submetida',
	Submitted = 'Submetida',
	SubmittedAndBlocked = 'Bloqueada',
	Approved = 'Aprovada',
	SubmittedWithoutChanges = 'Sub. sem alterações',
	AutoSubmitted = 'Sub. Automaticamente',
	Rejected = 'Recusada',
	Edited = 'Editada',
	ReadyForEdit = 'Em Edição',
}

export const TableTitle = memo(function TableTitle({ title1, title2 }: TableTitleInterface) {
	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 1,
			}}
		>
			<Typography
				sx={{
					fontWeight: 600,
					fontFamily: 'var(--font-family)',
					fontSize: '0.75rem',
					color: 'var(--primary-dark)',
				}}
				variant='subtitle2'
			>
				{title1}
			</Typography>
			<Typography
				sx={{
					fontWeight: 600,
					fontFamily: 'var(--font-family)',
					fontSize: '0.75rem',
					color: 'var(--primary-dark)',
				}}
				variant='subtitle2'
			>
				{title2}
			</Typography>
		</Box>
	)
})

export const TableTitleWithAscending = memo(function TableTitle({
	title1,
	title2,
	filter,
	order,
	activeColumn,
}: TableTitleInterface): React.JSX.Element {
	const isActive = activeColumn === order
	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 1,
			}}
		>
			<Typography
				sx={{
					fontWeight: 600,
					fontFamily: 'var(--font-family)',
					fontSize: '0.75rem',
					color: 'var(--primary-dark)',
				}}
				variant='subtitle2'
			>
				{title1}
			</Typography>
			<Typography
				sx={{
					fontWeight: 600,
					fontFamily: 'var(--font-family)',
					fontSize: '0.75rem',
					color: 'var(--primary-dark)',
				}}
				variant='subtitle2'
			>
				{title2}
			</Typography>
			{isActive && (
				<IconButton
					size='small'
					tabIndex={-1}
					aria-label={filter ? 'filter-ascending-true' : 'filter-ascending-false'}
					sx={{ padding: '0.1rem', marginInlineStart: '0.5rem' }}
				>
					{filter ? (
						<ArrowUpwardIcon
							sx={{ color: 'var(--primary-light)', width: '18px', height: '18px' }}
							fontSize='small'
						/>
					) : (
						<ArrowDownwardIcon
							sx={{ color: 'var(--primary-light)', width: '18px', height: '18px' }}
							fontSize='small'
						/>
					)}
				</IconButton>
			)}
		</Box>
	)
})

export const TableTitleWithOrder = memo(function TableTitle({
	title1,
	title2,
	filter,
	order,
	activeColumn,
}: TableTitleInterface): React.JSX.Element {
	const isActive = activeColumn === order

	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
			<Typography
				sx={{
					fontWeight: 600,
					fontFamily: 'var(--font-family)',
					fontSize: '0.75rem',
					color: 'var(--primary-dark)',
				}}
				variant='subtitle2'
			>
				{title1}
			</Typography>
			<Typography
				sx={{
					fontWeight: 600,
					fontFamily: 'var(--font-family)',
					fontSize: '0.75rem',
					color: 'var(--primary-dark)',
				}}
				variant='subtitle2'
			>
				{title2}
			</Typography>
			{isActive && (
				<IconButton
					size='small'
					tabIndex={-1}
					aria-label={filter ? 'filter-order-ascending' : 'filter-order-descending'}
					sx={{ padding: '0.1rem', marginInlineStart: '0.5rem' }}
				>
					{filter ? (
						<ArrowUpwardIcon
							sx={{ color: 'var(--primary-light)', width: '18px', height: '18px' }}
							fontSize='small'
						/>
					) : (
						<ArrowDownwardIcon
							sx={{ color: 'var(--primary-light)', width: '18px', height: '18px' }}
							fontSize='small'
						/>
					)}
				</IconButton>
			)}
		</Box>
	)
})

export const TableTitle2 = memo(function TableTitle2({ title1, title2 }: TableTitleInterface) {
	return (
		<>
			<Typography
				sx={{ fontWeight: 'bold', fontFamily: 'var(--font-family)', color: 'var(--primary-dark)' }}
				variant='subtitle2'
			>
				{title1}
			</Typography>
			<Typography
				variant='body2'
				sx={{ fontFamily: 'var(--font-family)', color: 'var(--primary-dark)' }}
			>
				{title2}
			</Typography>
		</>
	)
})

export const TableDoubleValues = memo(function TableDoubleValues({
	value1,
	value2,
}: {
	value1: string | number
	value2: string | number
}) {
	return (
		<Box sx={{ minWidth: '50px', width: '150px' }}>
			<Typography
				variant='subtitle2'
				sx={{ fontFamily: 'var(--font-family)', color: 'var(--primary-dark)' }}
			>
				{value1}
			</Typography>
			<Typography
				variant='subtitle2'
				sx={{ fontFamily: 'var(--font-family)', color: 'var(--primary-dark)' }}
			>
				{value2}
			</Typography>
		</Box>
	)
})

export const TableTimes = memo(function TableTimes({ times1, times2, times3 }: TableTimesInterface) {
	let color
	if (times1 !== times2) {
		color = 'red'
	}
	return (
		<>
			<Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
				<Typography
					variant='subtitle2'
					sx={{ fontFamily: 'var(--font-family)', color: 'var(--primary-dark)' }}
				>
					{times1}
				</Typography>
				<Typography
					sx={{ color: { color }, fontFamily: 'var(--font-family)' }}
					variant='subtitle2'
				>
					{times2}
				</Typography>
				<Typography
					variant='subtitle2'
					sx={{ fontFamily: 'var(--font-family)', color: 'var(--primary-dark)' }}
				>
					{times3}
				</Typography>
			</Box>
			<Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
				<Typography
					variant='subtitle2'
					sx={{ fontFamily: 'var(--font-family)', color: 'var(--primary-dark)' }}
				>
					{times1}
				</Typography>
				<Typography
					sx={{ color: { color }, fontFamily: 'var(--font-family)' }}
					variant='subtitle2'
				>
					{times2}
				</Typography>
				<Typography
					variant='subtitle2'
					sx={{ fontFamily: 'var(--font-family)', color: 'var(--primary-dark)' }}
				>
					{times3}
				</Typography>
			</Box>
		</>
	)
})

export const TableKms = memo(function TableKms({ kms1, kms2, kms3 }: TableKmsInterface) {
	let color
	if (kms1 !== kms2) {
		color = 'red'
	}
	return (
		<>
			<Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
				<Typography
					variant='subtitle2'
					sx={{ fontFamily: 'var(--font-family)' }}
				>
					{kms1}
				</Typography>
				<Typography
					sx={{ color: { color }, fontFamily: 'var(--font-family)' }}
					variant='subtitle2'
				>
					{kms2}
				</Typography>
				<Typography
					variant='subtitle2'
					sx={{ fontFamily: 'var(--font-family)' }}
				>
					{kms3}
				</Typography>
			</Box>
			<Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
				<Typography
					variant='subtitle2'
					sx={{ fontFamily: 'var(--font-family)' }}
				>
					{kms1}
				</Typography>
				<Typography
					sx={{ color: { color }, fontFamily: 'var(--font-family)' }}
					variant='subtitle2'
				>
					{kms2}
				</Typography>
				<Typography
					variant='subtitle2'
					sx={{ fontFamily: 'var(--font-family)', color: 'var(--primary-dark)' }}
				>
					{kms3}
				</Typography>
			</Box>
		</>
	)
})

export const TablePassengers = memo(function TablePassengers({ pass1, pass2, pass3 }: TablePassengersInterface) {
	let color
	if (pass1 !== pass2) {
		color = 'red'
	}
	return (
		<Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
			<Typography
				variant='subtitle2'
				sx={{ fontFamily: 'var(--font-family)', color: 'var(--primary-dark)' }}
			>
				{pass1}
			</Typography>
			<Typography
				sx={{ color: { color }, fontFamily: 'var(--font-family)' }}
				variant='subtitle2'
			>
				{pass2}
			</Typography>
			<Typography
				variant='subtitle2'
				sx={{ fontFamily: 'var(--font-family)', color: 'var(--primary-dark)' }}
			>
				{pass3}
			</Typography>
		</Box>
	)
})

export const TableMotorista = memo(function TableMotorista({ img, name, status }: TableMotoristaInterface) {
	let color = 'var(--primary)'
	if (status === 'Edited' || status === 'Approved') {
		color = 'var(--success)'
	}
	if (status === 'Edited' || status === 'ReadyForEdit' || status === 'NotSubmitted' || status === 'AutoSubmitted') {
		color = 'var(--warning)'
	}
	if (status === 'Rejected') {
		color = 'var(--error)'
	}
	if (img === '') {
		img = '/broken-image.jpg'
	}
	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				marginLeft: '-10px',
				'& .MuiDataGrid-columnHeader': {},
			}}
		>
			<Box padding={1}>
				<Avatar
					alt={name}
					variant='circular'
					sx={{
						borderRadius: '100%',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						bgcolor: 'lightgrey 50% / cover no-repeat',
						width: 32,
						height: 32,
						fontSize: '20px',
						fontWeight: 400,
						lineHeight: 1,
					}}
					src={img}
				/>
			</Box>
			<Box
				sx={{
					width: '9rem',
					display: 'grid',
					textOverflow: 'ellipsis',
				}}
			>
				<Typography
					variant='body2'
					sx={{
						fontSize: '0.75rem',
						color: 'var(--primary-dark)',
						fontWeight: 400,
						fontFamily: 'var(--font-family)',
					}}
				>
					{name}
				</Typography>
				<Typography
					variant='body2'
					sx={{
						fontSize: '0.6875rem',
						color: { color },
						fontWeight: 400,
						fontFamily: 'var(--font-family)',
						lineHeight: 1.3,
					}}
				>
					{Submit[status as keyof typeof Submit]}
				</Typography>
			</Box>
		</Box>
	)
})

export const InternalNumberCell = memo(function InternalNumberCell({ internalNumber }: { internalNumber: number }) {
	return (
		<Typography
			variant='body2'
			sx={{
				fontFamily: 'var(--font-family)',
				fontSize: '0.75rem',
				color: 'var(--primary-dark)',
				lineHeight: 1.3,
				fontWeight: 400,
			}}
		>
			{internalNumber}
		</Typography>
	)
})

export const TableData = memo(function TableData({ date, services }: TableDataInterface) {
	return (
		<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
			<Box sx={{ width: '120px', display: 'flex', flexDirection: 'column' }}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Typography
						sx={{
							fontFamily: 'var(--font-family)',
							fontSize: '0.75rem',
							color: 'var(--primary-dark)',
							lineHeight: 1.3,
							fontWeight: 400,
						}}
					>
						{dayjs(date).locale('pt').format('ddd DD/MM/YYYY')}
					</Typography>
				</LocalizationProvider>
				<Typography
					sx={{
						fontFamily: 'var(--font-family)',
						fontSize: '0.6875rem',
						color: 'var(--primary-dark)',
						lineHeight: 1.3,
						fontWeight: 400,
					}}
				>
					{services} serviços
				</Typography>
			</Box>
			<KeyboardArrowRightIcon sx={{ color: 'var(--primary)', padding: 0 }} />
		</Box>
	)
})

export const TableBackOfficeEndData = memo(function TableData() {
	return (
		<Box sx={{ display: 'flex', justifyContent: 'center' }}>
			<Box sx={{ paddingBlockStart: '0.5rem', paddingInlineStart: '1.5rem' }}>
				<KeyboardArrowRightIcon color='action' />
			</Box>
		</Box>
	)
})

export const TableTimer = memo(function TableTimer({
	initialTime,
	finalTime,
	/*  type, */ journey,
	initialFunction,
}: TableTimeInterface) {
	const localInitialTime = initialTime ? dayjs(initialTime) : null
	const [localFinalTime, setLocalFinalTime] = useState<Dayjs | null>(dayjs(finalTime))

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box sx={{ display: 'flex', flexDirection: 'column' }}>
				<Typography
					sx={{
						display: 'flex',
						justifyContent: 'center',
						marginBlock: '0.25rem',
						fontFamily: 'var(--font-family)',
						color: 'var(--primary-dark)',
					}}
					variant='subtitle2'
				>
					Jornada {`${journey}`}
				</Typography>
				<Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
					<TimeField
						sx={{ marginInline: '0.5rem', fontFamily: 'var(--font-family)' }}
						label='Entrada'
						size='small'
						value={localInitialTime}
						onChange={newValue => initialFunction(newValue)}
						format='HH:mm'
						disabled={initialTime ? false : true}
					/>
					<TimeField
						sx={{ marginInline: '0.5rem', fontFamily: 'var(--font-family)' }}
						label='Saída'
						size='small'
						value={localFinalTime}
						onChange={newValue => setLocalFinalTime(newValue)}
						format='HH:mm'
						disabled={finalTime ? false : true}
					/>
				</Box>
			</Box>
		</LocalizationProvider>
	)
})

export const TableDropdown = memo(function TableDropdown({ driverId, driverName }: Driver) {
	return (
		<MenuItem
			key={driverId}
			value={driverId}
			sx={{ fontFamily: 'var(--font-family)' }}
		>
			{driverName}
		</MenuItem>
	)
})
